import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import i18next from "src/i18n/config";
import { deleteApiKeys, getAccount, getApiKeys, getBillingCard, recreateApiKeys } from "src/services/accountService";
import { LocalStorage } from "src/services/localStorageService";
import PaymentService from "src/services/paymentService";
import LanguageSwitcher from "src/ui/containers/account/SettingsPage/Components/LanguageSwitcher";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { PropertyKey } from "@dashboard/shared/components/SidePanel/shared/Property";
import { TabContext, TabPanel } from "@mui/lab";
import { Box, Divider, List, ListItem, Tab } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import AuthService from "@services/authService";
import { getSettings } from "@services/configService";
import ApmLink from "@shared/ApmLink";
import { DangerButton, DangerOutlinedButton, PrimaryOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { type PageContentWidth,PageContentWrapper } from "@shared/PageContentWrapper/PageContentWrapper";
import { PageTabList } from "@shared/PageTabList/PageTabList";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

import { PasswordChangeForm } from "./ChangePassword/PasswordChangeForm";
import EditStripeCard from "./Components/EditStripeCard";
import { SettingsCard } from "./Components/SettingsCard";
import { TenantSwitcher } from "./Components/TenantSwitcher";
import { ThemeSwitcher } from "./Components/ThemeSwitcher";
import { DeleteAccountForm } from "./DeleteAccount/DeleteAccountForm";
import { BillingEditForm } from "./EditBilling/BillingEditForm";
import { ProfileEditForm } from "./EditProfile/ProfileEditForm";
import { TwoFactorDisableForm } from "./TwoFactorAuthorization/TwoFactorDisableForm";
import { TwoFactorEnableForm } from "./TwoFactorAuthorization/TwoFactorEnableForm";
import { useStyles } from "./SettingsPage.style";

const mediumWidthTabs = ["apikey", "security", "billing"] as const;

const getPageContentWidth = (tab: string): PageContentWidth =>
    mediumWidthTabs.includes(tab as (typeof mediumWidthTabs)[number]) ? "medium" : "large";

const getAppVersion = () => {
    const meta = document.querySelector("meta[name='version']");
    return meta?.getAttribute("content");
};

const SettingsPage = () => {
    const { t } = useTranslation(["common", "pages"]);
    const [profile, setProfile] = useState<API.Profile>();
    const [billingAddress, setBillingAddress] = useState<API.BillingAddress | undefined>();
    const [billingCard, setBillingCard] = useState<API.BillingCard | null>(null);

    const [apiKeys, setApiKeys] = useState<string | undefined>();
    const [isRemovingApiKey, setIsRemovingApiKey] = useState(false);
    const [isRenewApiKey, setIsRenewApiKey] = useState(false);
    const [isModalShown, setIsModalShown] = useState(false);
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
    const [stripePromise, setStripePromise] = useState<PromiseLike<Stripe | null> | Stripe | null>(null);

    const { features } = getSettings();
    const isStripeEnabled = features.stripeEnabled;

    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab");
    const modeFromUrl = searchParams.get("mode");
    const validTabs = ["portal", "profile", "security", "billing", "apikey"];
    const initialTab = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "portal";
    const [activeTab, setActiveTab] = useState(initialTab);

    const handleTabChange = useCallback(
        (_: React.SyntheticEvent, value: string) => {
            setActiveTab(value);
            setSearchParams({ tab: value }, { replace: true });
        },
        [setSearchParams],
    );

    const clearMode = useCallback(() => {
        setSearchParams({ tab: activeTab }, { replace: true });
    }, [activeTab, setSearchParams]);

    const loadBillingData = useCallback(async () => {
        try {
            const { data } = await getAccount();
            setProfile(data.profile);
            setBillingAddress(data?.billingAddress);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        }

        try {
            const apiKeysData = await getApiKeys();
            setApiKeys(apiKeysData);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        }
    }, []);

    const handleProfileEditSuccess = useCallback(() => {
        clearMode();
        void loadBillingData();
    }, [clearMode, loadBillingData]);

    const handleBillingEditSuccess = useCallback(() => {
        clearMode();
        void loadBillingData();
    }, [clearMode, loadBillingData]);

    const handleTwoFactorSuccess = useCallback(() => {
        clearMode();
        void loadBillingData();
    }, [clearMode, loadBillingData]);

    const { classes } = useStyles();

    const copyToClipboard = useCallback((text: string | undefined) => {
        if (!text) {
            return;
        }
        const sanitizedText = text.trim();
        navigator.clipboard
            .writeText(sanitizedText)
            .then(() => {
                showSuccessToast("Copied to clipboard");
            })
            .catch((_error) => {
                showErrorToast("Copy failed");
            });
    }, []);

    const handleApiKeysRenew = async () => {
        setIsRenewApiKey(true);

        try {
            const { model, message } = await recreateApiKeys();
            setApiKeys(model);
            showSuccessToast(message);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setIsRenewApiKey(false);
        }
    };
    const handleApiKeysDeletion = async () => {
        setIsRemovingApiKey(true);

        try {
            const { message } = await deleteApiKeys();
            showSuccessToast(message);
            setApiKeys("");
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setIsRemovingApiKey(false);
        }
    };

    const handleResetSettings = () => {
        LocalStorage.reset();
        showSuccessToast("Portal settings have been reset to default");
        window.location.reload();
    };
    const handleResetConfirm = () => {
        handleResetSettings();
    };

    useEffect(() => {
        const loadCardData = async () => {
            try {
                const _billingCard = await getBillingCard();
                setBillingCard(_billingCard);
                if (_billingCard !== null) {
                    const promiseData = await PaymentService.getStripePromise(loadStripe);
                    setStripePromise(promiseData as PromiseLike<Stripe | null> | Stripe | null);
                }
            } catch (_error) {
                showErrorToast("Failed retrieving billing card");
            }
        };
        if (isStripeEnabled) {
            void loadCardData();
        }
    }, [isStripeEnabled]);

    useEffect(() => {
        void loadBillingData();
    }, [loadBillingData]);

    useEffect(() => {
        if (tabFromUrl && validTabs.includes(tabFromUrl) && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl, activeTab]);

    if (!profile) {
        return null;
    }

    return (
        <PageContainer className={classes.container}>
            <Box>
                <PageHeader title={t("pages:settingsPage.title")} description={t("pages:settingsPage.description")} />
            </Box>

            <PageContentWrapper size={getPageContentWidth(activeTab)}>
                <TabContext value={activeTab}>
                    <PageTabList onChange={handleTabChange}>
                        <Tab
                            value="portal"
                            label={t("pages:settingsPage.portalSettings.title")}
                            icon={<i className="fa-solid fa-sliders" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="profile"
                            label={t("pages:settingsPage.profileData.title")}
                            icon={<i className="fa-solid fa-user" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="security"
                            label={t("pages:settingsPage.accountSecurity.title")}
                            icon={<i className="fa-solid fa-shield-halved" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="billing"
                            label={t("pages:settingsPage.billingAddress.title")}
                            icon={<i className="fa-solid fa-credit-card" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="apikey"
                            label={t("pages:settingsPage.apiKey.title")}
                            icon={<i className="fa-solid fa-key" />}
                            iconPosition="start"
                        />
                    </PageTabList>

                    <TabPanel value="portal" className={classes.tabPanel}>
                        <SettingsCard
                            title={t("pages:settingsPage.portalSettings.title")}
                            action={
                                <DangerOutlinedButton
                                    size="small"
                                    startIcon={<i className="fas fa-fw fa-undo" />}
                                    onClick={() => setIsResetConfirmOpen(true)}
                                    name="reset-settings"
                                >
                                    {t("pages:settingsPage.portalSettings.resetSettings.button")}
                                </DangerOutlinedButton>
                            }
                        >
                            <List className={classes.list}>
                                <ListItem>
                                    <Box className={classes.resetSettingsContainer}>
                                        <Box className={classes.switchContent}>
                                            <span className={classes.switchLabel}>{t("pages:settingsPage.language.label")}</span>
                                            <div>{t("pages:settingsPage.language.description")}</div>
                                        </Box>
                                        <Box className={classes.switcherBox}>
                                            <LanguageSwitcher showLabel={false} fullWidth={true} />
                                        </Box>
                                    </Box>
                                </ListItem>

                                <ListItem>
                                    <Box className={classes.resetSettingsContainer}>
                                        <Box className={classes.switchContent}>
                                            <span className={classes.switchLabel}>
                                                {t("pages:settingsPage.portalSettings.themeSwitch.label")}
                                            </span>
                                            <div>{t("pages:settingsPage.portalSettings.themeSwitch.description")}</div>
                                        </Box>
                                        <Box className={classes.switcherBox}>
                                            <ThemeSwitcher />
                                        </Box>
                                    </Box>
                                </ListItem>

                                {AuthService.mayUserSeeTenantSwitcher() ? (
                                    <ListItem>
                                        <Box className={classes.resetSettingsContainer}>
                                            <Box className={classes.switchContent}>
                                                <span className={classes.switchLabel}>
                                                    {t("pages:settingsPage.portalSettings.tenantSwitch.label")}
                                                </span>
                                                <div>{t("pages:settingsPage.portalSettings.tenantSwitch.description")}</div>
                                            </Box>
                                            <Box className={classes.switcherBox}>
                                                <TenantSwitcher />
                                            </Box>
                                        </Box>
                                    </ListItem>
                                ) : null}
                            </List>
                        </SettingsCard>
                    </TabPanel>

                    <TabPanel value="profile" className={classes.tabPanel}>
                        {modeFromUrl === "edit" ? (
                            <ProfileEditForm profile={profile!} onSuccess={handleProfileEditSuccess} onCancel={clearMode} />
                        ) : modeFromUrl === "delete" ? (
                            <DeleteAccountForm onCancel={clearMode} />
                        ) : (
                            <SettingsCard
                                title={t("pages:settingsPage.profileData.title")}
                                action={
                                    <>
                                        <Link to="/account/settings?tab=profile&mode=edit">
                                            <PrimaryOutlinedButton
                                                size="small"
                                                startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                                name="edit-profile"
                                            >
                                                {t("common:button.edit")}
                                            </PrimaryOutlinedButton>
                                        </Link>
                                        <Link to="/account/settings?tab=profile&mode=delete">
                                            <DangerOutlinedButton
                                                size="small"
                                                startIcon={<i className="fas fa-fw fa-trash-alt" />}
                                                name="delete-profile"
                                            >
                                                {t("pages:settingsPage.profileData.delete")}
                                            </DangerOutlinedButton>
                                        </Link>
                                    </>
                                }
                            >
                                <List>
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.email")}</PropertyKey>
                                        <span className={classes.valueColumn}>{profile?.email}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.authentication")}</PropertyKey>
                                        <span className={classes.valueColumn}>
                                            {profile?.twoFactorEnabled ? (
                                                <span>{t("pages:settingsPage.profileData.twoFactor")}</span>
                                            ) : (
                                                <span>{t("common:label.password")}</span>
                                            )}
                                        </span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.name")}</PropertyKey>
                                        <span className={classes.valueColumn}>{profile?.name}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.phone")}</PropertyKey>
                                        <span className={classes.valueColumn}>
                                            {profile?.phoneCode}
                                            {profile?.phoneNumber}
                                        </span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("common:label.address")}</PropertyKey>
                                        <span className={classes.valueColumn}>{profile?.address}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.city")}</PropertyKey>
                                        <span className={clsx(classes.valueColumn, classes.capitalizedColumn)}>
                                            {profile?.city}
                                        </span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.zipCode")}</PropertyKey>
                                        <span className={classes.valueColumn}>{profile?.zipCode}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.country")}</PropertyKey>
                                        <span className={classes.valueColumn}>{profile?.country}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.notifications")}</PropertyKey>
                                        <span className={classes.valueColumn}>
                                            {profile?.newsletterChecked ? (
                                                <span>{t("pages:settingsPage.profileData.extended")}</span>
                                            ) : (
                                                <span>{t("pages:settingsPage.profileData.standard")}</span>
                                            )}
                                        </span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.usage")}</PropertyKey>
                                        <span className={classes.valueColumn}>
                                            {profile?.typeOfUsage === "b" ? (
                                                <span>{t("pages:settingsPage.profileData.businessUse")}</span>
                                            ) : (
                                                <span>{t("pages:settingsPage.profileData.nonBusinessUse")}</span>
                                            )}
                                        </span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.company")}</PropertyKey>
                                        <span className={classes.valueColumn}>{profile?.companyName}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.industry")}</PropertyKey>
                                        <span className={classes.valueColumn}>{profile?.fieldOfApplication}</span>
                                    </ListItem>
                                </List>
                            </SettingsCard>
                        )}
                    </TabPanel>

                    <TabPanel value="security" className={classes.tabPanel}>
                        {modeFromUrl === "password" ? (
                            <PasswordChangeForm onCancel={clearMode} />
                        ) : modeFromUrl === "2fa-enable" ? (
                            <TwoFactorEnableForm onSuccess={handleTwoFactorSuccess} onCancel={clearMode} />
                        ) : modeFromUrl === "2fa-disable" ? (
                            <TwoFactorDisableForm onSuccess={handleTwoFactorSuccess} onCancel={clearMode} />
                        ) : (
                            <SettingsCard title={t("pages:settingsPage.accountSecurity.title")}>
                                <List className={classes.list}>
                                    <ListItem>
                                        <Box className={classes.resetSettingsContainer}>
                                            <Box className={classes.switchContent}>
                                                <span className={classes.switchLabel}>
                                                    {t("pages:settingsPage.accountSecurity.accountPassword")}
                                                </span>
                                                <div>{t("pages:settingsPage.accountSecurity.accountPasswordDescription")}</div>
                                            </Box>
                                            <ApmLink
                                                parent="Account security settings"
                                                title={t("common:button.change")}
                                                role="button"
                                                to="/account/settings?tab=security&mode=password"
                                            >
                                                <PrimaryOutlinedButton
                                                    size="small"
                                                    startIcon={<i className="fa-solid fa-fw fa-key" />}
                                                >
                                                    {t("common:button.change")}
                                                </PrimaryOutlinedButton>
                                            </ApmLink>
                                        </Box>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <Box className={classes.resetSettingsContainer}>
                                            <Box className={classes.switchContent}>
                                                <span className={classes.switchLabel}>
                                                    {t("pages:settingsPage.accountSecurity.twoFactor")}
                                                </span>
                                                <div>{t("pages:settingsPage.accountSecurity.twoFactorDescription")}</div>
                                            </Box>
                                            {profile?.twoFactorEnabled ? (
                                                <ApmLink
                                                    parent="Account security settings"
                                                    title="Disable 2FA"
                                                    role="button"
                                                    to="/account/settings?tab=security&mode=2fa-disable"
                                                >
                                                    <SecondaryButton size="small">{t("common:button.disable")}</SecondaryButton>
                                                </ApmLink>
                                            ) : (
                                                <ApmLink
                                                    parent="Account security settings"
                                                    title="Enable 2FA"
                                                    role="button"
                                                    to="/account/settings?tab=security&mode=2fa-enable"
                                                >
                                                    <PrimaryOutlinedButton
                                                        size="small"
                                                        startIcon={<i className="fa-solid fa-shield-halved" />}
                                                    >
                                                        {t("common:button.enable")}
                                                    </PrimaryOutlinedButton>
                                                </ApmLink>
                                            )}
                                        </Box>
                                    </ListItem>
                                </List>
                            </SettingsCard>
                        )}
                    </TabPanel>

                    <TabPanel value="billing" className={classes.tabPanelBilling}>
                        {modeFromUrl === "edit" ? (
                            <BillingEditForm
                                billingAddress={billingAddress}
                                onSuccess={handleBillingEditSuccess}
                                onCancel={clearMode}
                            />
                        ) : (
                            <SettingsCard
                                title={t("pages:settingsPage.billingAddress.title")}
                                action={
                                    <Link to="/account/settings?tab=billing&mode=edit">
                                        <PrimaryOutlinedButton
                                            size="small"
                                            startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                            name="edit-billing-address"
                                        >
                                            {t("common:button.edit")}
                                        </PrimaryOutlinedButton>
                                    </Link>
                                }
                            >
                                <List>
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.name")}</PropertyKey>
                                        <span className={classes.valueColumn}>{billingAddress?.name}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("common:label.address")}</PropertyKey>
                                        <span className={classes.valueColumn}>{billingAddress?.address}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.city")}</PropertyKey>
                                        <span className={classes.valueColumn}>{billingAddress?.city}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.zipCode")}</PropertyKey>
                                        <span className={classes.valueColumn}>{billingAddress?.zipCode}</span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.profileData.country")}</PropertyKey>
                                        <span className={classes.valueColumn}>{billingAddress?.country}</span>
                                    </ListItem>
                                </List>
                            </SettingsCard>
                        )}
                        {isStripeEnabled ? (
                            <SettingsCard
                                title={t("pages:settingsPage.paymentMethod.title")}
                                action={
                                    <Tooltip placement="top" title={t("pages:settingsPage.paymentMethod.editTooltip")}>
                                        <PrimaryOutlinedButton
                                            startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                            onClick={() => setIsModalShown(true)}
                                            size="small"
                                            disabled
                                            name="edit-payment-method"
                                        >
                                            {t("common:button.edit")}
                                        </PrimaryOutlinedButton>
                                    </Tooltip>
                                }
                            >
                                <List>
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.paymentMethod.cardType")}</PropertyKey>
                                        <span className={clsx(classes.valueColumn, classes.capitalizedColumn)}>
                                            {billingCard?.brand ?? t("pages:settingsPage.paymentMethod.noCardSet")}
                                        </span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("pages:settingsPage.paymentMethod.cardNumber")}</PropertyKey>
                                        <span className={classes.valueColumn}>
                                            {billingCard?.lastFourDigits
                                                ? `**** **** **** ${billingCard?.lastFourDigits}`
                                                : t("pages:settingsPage.paymentMethod.noCardNumber")}
                                        </span>
                                    </ListItem>
                                    <Divider />
                                    <ListItem>
                                        <PropertyKey>{t("common:label.expirationDate")}</PropertyKey>
                                        <span className={classes.valueColumn}>
                                            {billingCard?.expMonth ?? "MM"} / {billingCard?.expYear ?? "YY"}
                                        </span>
                                    </ListItem>
                                </List>
                            </SettingsCard>
                        ) : null}
                        {stripePromise && isStripeEnabled ? (
                            <Elements stripe={stripePromise}>
                                <EditStripeCard open={isModalShown} onClose={() => setIsModalShown(false)} />
                            </Elements>
                        ) : null}
                    </TabPanel>

                    <TabPanel value="apikey" className={classes.tabPanel}>
                        <SettingsCard title={t("pages:settingsPage.apiKey.title")}>
                            <List>
                                <ListItem>
                                    <div className={classes.apiKeyContainer}>
                                        {apiKeys ? (
                                            <>
                                                <span>{apiKeys}</span>
                                                <div className={classes.apiKeyActions}>
                                                    <SecondaryButton
                                                        className={classes.copyButton}
                                                        startIcon={<i className="fa-solid fa-copy" />}
                                                        name="copy-api-key"
                                                        onClick={() => copyToClipboard(apiKeys)}
                                                    >
                                                        {t("common:button.copy")}
                                                    </SecondaryButton>
                                                    <SecondaryButton
                                                        startIcon={<i className="fa-solid fa-arrow-rotate-left" />}
                                                        name="renew-api-key"
                                                        onClick={() => handleApiKeysRenew()}
                                                        loading={isRenewApiKey}
                                                        disabled={isRenewApiKey || isRemovingApiKey}
                                                        size="small"
                                                    >
                                                        {t("pages:settingsPage.apiKey.renew")}
                                                    </SecondaryButton>
                                                    <DangerButton
                                                        disabled={!apiKeys || isRemovingApiKey || isRenewApiKey}
                                                        size="small"
                                                        startIcon={<i className="fas fa-fw fa-trash-alt" />}
                                                        name="delete-api-key"
                                                        onClick={() => handleApiKeysDeletion()}
                                                        loading={isRemovingApiKey}
                                                    >
                                                        {t("common:button.delete")}
                                                    </DangerButton>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span>{t("pages:settingsPage.apiKey.noKey")}</span>
                                                <PrimaryOutlinedButton
                                                    className={classes.generateButton}
                                                    startIcon={<i className="fa-solid fa-key" />}
                                                    onClick={() => handleApiKeysRenew()}
                                                    loading={isRenewApiKey}
                                                    disabled={isRenewApiKey || isRemovingApiKey}
                                                    size="small"
                                                    name="generate-api-key"
                                                >
                                                    {t("common:button.generate")}
                                                </PrimaryOutlinedButton>
                                            </>
                                        )}
                                    </div>
                                </ListItem>
                            </List>
                        </SettingsCard>
                    </TabPanel>
                </TabContext>
            </PageContentWrapper>

            <Box className={classes.versionDisplay}>
                {t("pages:settingsPage.version")}: {getAppVersion()}
            </Box>

            <ConfirmDialog
                open={isResetConfirmOpen}
                onClose={() => setIsResetConfirmOpen(false)}
                onConfirm={handleResetConfirm}
                title={t("pages:settingsPage.portalSettings.resetSettings.confirmModal.title")}
                primaryActionText={t("pages:settingsPage.portalSettings.resetSettings.confirmModal.confirmButton")}
                dangerButton
            >
                <Box component="p" className={classes.confirmDialogParagraph}>
                    {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.description")}
                </Box>
                <Box component="p" className={classes.confirmDialogListTitle}>
                    {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeReset")}
                </Box>
                <Box component="ul" className={classes.confirmDialogList}>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.languageAndTheme")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.uiPreferences")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.tableSettings")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.collapsedSections")}
                    </li>
                </Box>
            </ConfirmDialog>
        </PageContainer>
    );
};

export default withMetaDescription(SettingsPage, {
    title: i18next.t("pages:settingsPage.metaTitle"),
    description: i18next.t("pages:settingsPage.metaDescription"),
});
